import { ShouldRevalidateFunctionArgs } from "react-router-dom";
import _ from "lodash";

import scraperApi, { ApiError, ApiErrorResponse } from "api";

import { addToasterActionData, IToasterActionData } from "hooks/useToasterFromActionData";

import { RouterActionResponse } from "routes/dataroutes/RouterActionResponse";
import OkResponse from "routes/dataroutes/OkResponse";

import { formDataToStructuredObject } from "utils/formDataUtils";

import { PaymentSource, toPaymentSourceRequest } from "types/billing/PaymentSource";

import {
  InvoicesLoaderDataType,
  PaginatedInvoicesLoaderDataType
} from "v2/hooks/billing/useInvoices";
import { RouteErrors } from "routes/data-routes/types";


export const FormInputName = {
  action: "form-action",
};

export function formAction(actionValue: string) {
  return {
    name: FormInputName.action,
    value: actionValue
  };
}

export const BillingInputName = {
  autoRenewalSetting: "auto-renewal-setting",
  billingAddress: "billing-address",
  chargebeeSubscriptionId: "chargebee-subscription-id",
  keepScheduledChanges: "keep-scheduled-changes",
  operationId: "billing-operation-id",
  paymentSource: "payment-source",
  showCancellationSurvey: "show-cancellation-survey",
  targetPlanSlug: "target-plan-slug",
};

export const CouponInputName = {
  activeCouponCode: "active-coupon-code",
  couponCode: "coupon-code",
};

export const CouponActions = {
  add_coupon: formAction("add-coupon"),
  remove_coupon: formAction("remove-coupon")
};

export const PaymentSourceInputName = {
  action: "payment-source-action",

  id: "payment-source-id",
  type: "payment-source-type",

  card: {
    number: "card-number",
    expiry: {
      month: "card-expiry.month",
      year: "card-expiry.year",
      fullYear: "card-expiry.fullYear"
    },
    cvv: "card-cvv",
    nameOnCard: "name-on-card",
  },

  paypal: {
    billingAgreementToken: "billing-agreement-token",
  },

  primary: "primary",
  newPrimaryId: "new-primary-id"
};

function paymentSourceFormAction(actionValue: string) {
  return {
    name: PaymentSourceInputName.action,
    value: actionValue
  };
}

export const PaymentSourceActions = {
  add_or_update: paymentSourceFormAction("add-or-update"),
  add_paypal: paymentSourceFormAction("add-paypal"),
  remove: paymentSourceFormAction("remove"),
  remove_primary_with_cancel: paymentSourceFormAction("remove-primary-with-cancel"),
  remove_with_promotion: paymentSourceFormAction("remove-with-promotion"),
  set_primary: paymentSourceFormAction("set-primary"),
};

// TODO this should not be declared here
export type PaymentSourceActionData = {
  paymentSources: PaymentSource[];
} & IToasterActionData;


// Error codes the billing service emits when a coupon is invalid / not usable.
// Handled explicitly so a bad coupon surfaces as a modal-scoped error instead
// of bubbling up to the route error boundary (which renders NotFound).
export const COUPON_ERROR_CODES = new Set([
  "err_coupon_not_supported",
  "err_coupon_not_active",
  "err_coupon_not_applicable",
  "err_fp_coupon_mismatch",
  "err_fp_user_not_included",
  "err_fp_user_denied",
  "err_fp_promo_not_active",
  "err_fp_no_offer",
]);


export async function billingAddressLoader({ request }: { request: Request }) {
  const billingAddress = await scraperApi.billing.getBillingAddress({ signal: request.signal });
  return billingAddress || {};
}

const PAYMENT_SOURCE_STATUSES = ["valid", "expiring", "pending_verification", "expired", "invalid"];
const paymentSourceStatusParams = () => new URLSearchParams(PAYMENT_SOURCE_STATUSES.map(s => ["status", s]));

export async function paymentSourcesLoader({ request }: { request: Request }): Promise<PaymentSource[]> {
  const paymentSources = await scraperApi.billing.paymentSources.getPaymentSources({ params: paymentSourceStatusParams(), signal: request.signal });

  return paymentSources || [];
}

async function addOrUpdatePaymentSourceAction(formData: FormData): Promise<RouterActionResponse<PaymentSourceActionData>> {
  try {
    const paymentSourceRequest = toPaymentSourceRequest(formData);
    const isPaymentSourceUpdate = paymentSourceRequest.id !== undefined && paymentSourceRequest.id !== "";
    const makePrimary = !isPaymentSourceUpdate && Boolean(formData.get(PaymentSourceInputName.primary));

    let existingIds: string[] = [];
    if (makePrimary) {
      existingIds = (await scraperApi.billing.paymentSources.getPaymentSources({ params: paymentSourceStatusParams() }))?.map(s => s.id) || [];
    }

    const updatedPaymentSources = await scraperApi.billing.paymentSources.addOrUpdatePaymentSource(paymentSourceRequest);
    let paymentSources = updatedPaymentSources || [];

    // The card is added at this point (the add response only returns valid sources), so
    // set-primary is best-effort: a failure here shouldn't fail the whole add.
    if (makePrimary) {
      const newSource = updatedPaymentSources?.find(s => !existingIds.includes(s.id));
      if (newSource && !newSource.isPrimary) {
        try {
          await scraperApi.billing.setPrimaryPaymentSource(newSource.id);
          const refreshedSources = await scraperApi.billing.paymentSources.getPaymentSources({ params: paymentSourceStatusParams() });
          paymentSources = refreshedSources || [];
        } catch {
          // Re-fetch so the returned list reflects the true current state, then warn.
          try {
            const refreshedSources = await scraperApi.billing.paymentSources.getPaymentSources({ params: paymentSourceStatusParams() });
            paymentSources = refreshedSources || [];
          } catch { /* keep the add-response list */ }

          return addToasterActionData({ paymentSources }, {
            toastId: "payment-source-added-not-primary",
            type: "warning",
            title: "Payment Method Added",
            description: "Your card was added, but we couldn't set it as your default payment method."
          });
        }
      }
    }

    return addToasterActionData({ paymentSources }, {
      toastId: isPaymentSourceUpdate ? "payment-source-updated" : "payment-source-added",
      type: "success",
      title: isPaymentSourceUpdate ? "Payment Method Updated" : "Payment Method Added",
      description: isPaymentSourceUpdate
        ? "Your payment method has been updated successfully."
        : "Your new payment method has been added successfully."
    });
  } catch (err) {
    const error = err as ApiError<ApiErrorResponse>;

    const errors: { [index: string]: string } = {};

    if ((error.error_code === "chargebee_error") && (error.details.chargebee_api_error_code === "payment_method_verification_failed")) {

      if ((error.details.chargebee_error_code === "add_card_error") && ((error.details.message as string).toLowerCase().endsWith("expired card."))) {
        errors["expiry_year"] = "Your card has expired";
      } else {
        const invalidFieldMatcher = (error.details.message as string).match(/^card\[(\w+)] : (.*)/);
        if (invalidFieldMatcher && invalidFieldMatcher[1]) {
          errors[invalidFieldMatcher[1]] = invalidFieldMatcher[2] || "Invalid value";
        }
      }

      return addToasterActionData(
        {
          error: {
            formInputErrors: errors
          }
        },
        {
          toastId: "payment-source-verification-failed",
          type: "error",
          title: "Verification Failed",
          description: "Your payment method could not be verified. Please check your details and try again."
        }
      );
    }

    return addToasterActionData(
      {
        error: {
          taggedMessage: {
            message: "There was an error adding your payment method. Please try again later or [contact our support team|contact_support]"
          }
        }
      },
      {
        toastId: "payment-source-add-error",
        type: "error",
        title: "Error",
        description: "Failed to add payment method. Please try again."
      }
    );
  }
}

async function removePaymentSourceAction(paymentSourceId: string): Promise<RouterActionResponse<PaymentSourceActionData>> {
  try {
    const updatedPaymentSources = await scraperApi.billing.paymentSources.deletePaymentSource(paymentSourceId);
    const paymentSources = updatedPaymentSources || [];
    return addToasterActionData({ paymentSources }, {
      toastId: "payment-source-removed",
      type: "success",
      title: "Payment Method Removed",
      description: "Your payment method has been removed successfully."
    });
  } catch (err) {
    return addToasterActionData(
      {
        error: {
          taggedMessage: {
            message: "There was an error removing your payment method. Please try again later or [contact our support team|contact_support]"
          }
        }
      },
      {
        toastId: "remove-payment-source-error",
        type: "error",
        title: "Error",
        description: "Failed to remove payment method. Please try again."
      }
    );
  }
}

// For PLG paid users removing their primary card: schedule end-of-term cancellation
// first, then delete the card. Cancel-first is chosen so a failure leaves the card
// intact and the subscription untouched — the reverse order could strand a paid
// subscription with no payment method if the cancel call then failed.
async function removePrimaryWithCancelAction(paymentSourceId: string): Promise<RouterActionResponse<PaymentSourceActionData>> {
  try {
    await scraperApi.subscription.cancel();
  } catch (err) {
    return addToasterActionData(
      {
        error: {
          taggedMessage: {
            message: "We couldn't schedule your subscription cancellation, so your payment method was not removed. Please try again later or [contact our support team|contact_support]"
          }
        }
      },
      {
        toastId: "remove-primary-cancel-error",
        type: "error",
        title: "Error",
        description: "Failed to schedule cancellation. Your payment method was not removed."
      }
    );
  }

  try {
    const updatedPaymentSources = await scraperApi.billing.paymentSources.deletePaymentSource(paymentSourceId);
    const paymentSources = updatedPaymentSources || [];
    return addToasterActionData({ paymentSources }, {
      toastId: "payment-source-removed",
      type: "success",
      title: "Subscription Cancellation Scheduled",
      description: "Your subscription will be cancelled at the end of the current billing period and your payment method has been removed."
    });
  } catch (err) {
    // Cancellation was scheduled successfully but the delete failed — surface
    // the partial state so the user can retry or reactivate from Manage Plan.
    return addToasterActionData(
      {
        error: {
          taggedMessage: {
            message: "Your subscription cancellation was scheduled but we couldn't remove the payment method. Please try removing it again or [contact our support team|contact_support]"
          }
        }
      },
      {
        toastId: "remove-primary-partial-error",
        type: "error",
        title: "Payment Method Not Removed",
        description: "Cancellation scheduled, but payment method removal failed."
      }
    );
  }
}

// When a user removes their current primary payment source but has a usable backup,
// promote the backup to primary first, then delete the old source. Preserves the
// subscription — no cancellation is scheduled. Backup-first ordering means a
// setPrimary failure leaves the original card intact; a delete failure after a
// successful promotion leaves a recoverable state (backup is now primary, user
// can retry removing the old card).
async function removeWithPromotionAction(paymentSourceId: string, newPrimaryId: string): Promise<RouterActionResponse<PaymentSourceActionData>> {
  if (!newPrimaryId || newPrimaryId === paymentSourceId) {
    // Guard against a tampered payload that would promote and then immediately
    // delete the same card. Fall through to the normal remove path from the UI.
    return removePaymentSourceAction(paymentSourceId);
  }

  try {
    await scraperApi.billing.setPrimaryPaymentSource(newPrimaryId);
  } catch (err) {
    return addToasterActionData(
      {
        error: {
          taggedMessage: {
            message: "We couldn't update your default payment method, so the card was not removed. Please try again later or [contact our support team|contact_support]"
          }
        }
      },
      {
        toastId: "remove-promotion-set-primary-error",
        type: "error",
        title: "Error",
        description: "Failed to update default payment method. Your card was not removed."
      }
    );
  }

  try {
    const updatedPaymentSources = await scraperApi.billing.paymentSources.deletePaymentSource(paymentSourceId);
    const paymentSources = updatedPaymentSources || [];
    return addToasterActionData({ paymentSources }, {
      toastId: "payment-source-removed",
      type: "success",
      title: "Payment Method Removed",
      description: "Your default payment method was updated and the old one was removed."
    });
  } catch (err) {
    return addToasterActionData(
      {
        error: {
          taggedMessage: {
            message: "Your default payment method was updated but we couldn't remove the old one. Please try removing it again or [contact our support team|contact_support]"
          }
        }
      },
      {
        toastId: "remove-promotion-delete-error",
        type: "error",
        title: "Payment Method Not Removed",
        description: "Default method updated, but old card removal failed."
      }
    );
  }
}

async function setPrimaryPaymentSourceAction(paymentSourceId: string): Promise<RouterActionResponse<PaymentSourceActionData>> {
  try {
    await scraperApi.billing.setPrimaryPaymentSource(paymentSourceId);
    const updatedPaymentSources = await scraperApi.billing.paymentSources.getPaymentSources({ params: paymentSourceStatusParams() });
    const paymentSources = updatedPaymentSources || [];
    return addToasterActionData({ paymentSources }, {
      toastId: "payment-source-set-primary",
      type: "success",
      title: "Default Payment Method Updated",
      description: "Your default payment method has been updated successfully."
    });
  } catch (err) {
    return addToasterActionData(
      {
        error: {
          taggedMessage: {
            message: "There was an error updating your default payment method. Please try again later or [contact our support team|contact_support]"
          }
        }
      },
      {
        toastId: "set-primary-payment-source-error",
        type: "error",
        title: "Error",
        description: "Failed to update default payment method. Please try again."
      }
    );
  }
}

async function addPayPalPaymentSourceAction(formData: FormData): Promise<RouterActionResponse<PaymentSourceActionData>> {
  try {
    const billingAgreementToken = formData.get(PaymentSourceInputName.paypal.billingAgreementToken) as string;
    const updatedPaymentSources = await scraperApi.billing.paymentSources.addPayPalPaymentSource(billingAgreementToken);
    const paymentSources = updatedPaymentSources || [];

    return addToasterActionData({ paymentSources }, {
      toastId: "paypal-payment-source-added",
      type: "success",
      title: "Payment Method Added",
      description: "Your PayPal payment method has been added successfully."
    });
  } catch (err) {
    const error = err as ApiError<ApiErrorResponse>;
    return addToasterActionData(
      { error: { message: error.message || "Failed to add PayPal payment method" } },
      {
        toastId: "paypal-payment-source-error",
        type: "error",
        title: "Payment Method Error",
        description: error.message || "Failed to add PayPal payment method"
      }
    );
  }
}

// TODO inconsistent return types
export async function paymentSourcesActions({ request }: { request: Request }): Promise<RouterActionResponse<PaymentSourceActionData | PaymentSource[]>> {
  const formData = await request.formData();

  if (formData.get(PaymentSourceInputName.action) === PaymentSourceActions.add_or_update.value) {
    return addOrUpdatePaymentSourceAction(formData);
  }

  if (formData.get(PaymentSourceInputName.action) === PaymentSourceActions.add_paypal.value) {
    return addPayPalPaymentSourceAction(formData);
  }

  const formDataObj = formDataToStructuredObject(formData);

  switch (formData.get(PaymentSourceInputName.action)) {
    case PaymentSourceActions.remove.value:
      return removePaymentSourceAction(_.get(formDataObj, PaymentSourceInputName.id));
    case PaymentSourceActions.remove_primary_with_cancel.value:
      return removePrimaryWithCancelAction(_.get(formDataObj, PaymentSourceInputName.id));
    case PaymentSourceActions.remove_with_promotion.value:
      return removeWithPromotionAction(
        _.get(formDataObj, PaymentSourceInputName.id),
        _.get(formDataObj, PaymentSourceInputName.newPrimaryId)
      );
    case PaymentSourceActions.set_primary.value:
      return setPrimaryPaymentSourceAction(_.get(formDataObj, PaymentSourceInputName.id));
  }

  throw new Error("Unsupported action: " + formData.get(PaymentSourceInputName.action));
}

export function paymentSourcesShouldRevalidate(args: ShouldRevalidateFunctionArgs) {
  // TODO implement this to revalidate properly
  //  check args.actionResult to see whether the action was successful and we need to reload here
  //  payment sources should be reloaded if the user was changed after reauthentication
  return args.defaultShouldRevalidate;
}

export async function allPaymentMethodsLoader({ request }: { request: Request }) {
  const paymentMethods = await scraperApi.subscription.allPaymentMethods({ signal: request.signal });
  return paymentMethods;
}

export async function setAutoRenewalTo100Action({ request }: { request: Request }): Promise<Response | RouteErrors.RouterActionError> {
  try {
    await scraperApi.subscription.changeAutoRenewal("100", { signal: request.signal });
    return new OkResponse();
  } catch (err) {
    // TODO proper error handling needed
    const apiError = err as ApiError<ApiErrorResponse>;

    if (apiError.error_code) {
      return {
        error: {
          message: apiError.message
        }
      };
    }

    // bubble up
    throw err;
  }
}

export async function notPaidInvoicesLoader({ request }: { request: Request }) {
  return {
    notPaidInvoicesPromise: scraperApi.billing.invoices.getUnpaidInvoicesCount({ signal: request.signal })
      .then(({ count }) => {
        if (count > 0) {
          throw new Error("User has unpaid invoices")
        } else {
          return count;
        }
      })
  };
}

// TODO move it out of BillingData, it's an account-related action
export async function requestAccountDeletionAction({ request }: { request: Request }): Promise<{ response: string } & IToasterActionData> {
  try {
    const response = await scraperApi.auth.requestDeletion({ signal: request.signal });

    return addToasterActionData({ response }, {
      toastId: "deletion-request-success",
      type: "success",
      title: "Account Deletion Requested",
      description: response === "deleted_permanently" ?
        "Your request to delete your account was successful. In a few seconds you will be logged out and can not log in anymore." :
        "You sent a request to our support team to delete your account. Our team will send you a confirmation email, when your account was deleted."
    });
  } catch (error) {
    return addToasterActionData({ response: "error" }, {
      toastId: "deletion-request-fail",
      type: "error",
      title: "Requesting Account Deletion Failed",
      description: "There was an issue with your request. Please try again later or contact our support team."
    });
  }
}

export async function billingInfoLoader({ request }: { request: Request }) {
  const [ billingAddress, paymentSources, paymentMethods ] = await Promise.all([
    billingAddressLoader({ request }),
    // TODO paymentSourcesLoader and allPaymentMethodsLoader will fetch the same data twice, with a small addition that
    //  allPaymentMethodsLoader will also get the virtual bank accounts of the user
    paymentSourcesLoader({ request }),
    allPaymentMethodsLoader({ request })
  ]);

  return {
    billingAddress,
    paymentSources,
    virtualBankAccount: paymentMethods?.virtualBankAccount
  };
}

export async function countersLoader({ request }: { request: Request }) {
  const counters = await scraperApi.stats.counters({ signal: request.signal });
  return counters;
}

/**
 * Original loader — fetches all invoices without pagination.
 * Used by useInvoices (UpgradeSubscriptionModal refresh).
 */
export async function invoicesLoader({ request }: { request: Request }): Promise<InvoicesLoaderDataType> {
  return {
    invoices: await scraperApi.billing.invoices.getInvoices({ signal: request.signal })
  };
}

/**
 * Paginated loader — fetches invoices with server-side pagination and status filtering.
 * Used by useInvoicesPaginated (PaymentHistoryCard).
 */
export async function invoicesPaginatedLoader({ request }: { request: Request }): Promise<PaginatedInvoicesLoaderDataType> {
  const url = new URL(request.url);
  const page = url.searchParams.get('page');
  const pageSize = url.searchParams.get('pageSize');
  const status = url.searchParams.get('status');

  return {
    invoices: await scraperApi.billing.invoices.getInvoicesPaginated(
      {
        ...(page && { page: Number(page) }),
        ...(pageSize && { pageSize: Number(pageSize) }),
        ...(status && { status }),
      },
      { signal: request.signal }
    )
  };
}
